import React, { FormEvent, useEffect, useMemo, useState } from "react";

import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";

import { Redirect } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Row,
  UncontrolledAlert,
} from "reactstrap";
import { parse } from "qs";

import { setPageTitle } from "helpers/page-title";
import imageSrc from "assets/img/fetch-package-logo-full-white.svg";
import { BEM } from "helpers/BEM.helper";
import { useFetchSelector } from "helpers/hooks/useFetchSelector.hook";
import { isValidEmail } from "helpers/validateEmail";
import { authentication as authenticationSlice } from "slices/authentication";

const classes = new BEM({
  block: { name: "login" },
  elements: [
    { name: "email" },
    { name: "password" },
    { name: "login", extras: ["btn-info", "mb-3"] },
    { name: "register" },
    {
      name: "forgotPassword",
      extras: ["text-white", "d-flex", "justify-content-center"],
    },
  ],
});

export const Login: React.FC = () => {
  const location = useLocation();
  const parsedQueryString = parse(location.search, { ignoreQueryPrefix: true });
  const dispatch = useDispatch();
  const [email, setEmail] = useState(
    typeof parsedQueryString?.email === "string" ? parsedQueryString.email : "",
  );
  const authentication = useFetchSelector("authentication");
  const loggedIn = authentication?.loggedIn;
  const loginStatus = authentication?.loginStatus;

  const [password, setPassword] = useState(
    typeof parsedQueryString?.password === "string"
      ? parsedQueryString.password
      : "",
  );

  // used to handle automatic login situation and prevent duplicate login requests
  const [canAttemptAutomaticLogin, setCanAttemptAutomaticLogin] = useState(
    typeof parsedQueryString?.password === "string" &&
      typeof parsedQueryString?.email === "string",
  );

  useEffect(() => {
    setPageTitle("Login");
    document.body.classList.toggle("login-page");
    return () => {
      document.body.classList.toggle("login-page");
    };
  }, []);

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (email && password) {
      if (!isValidEmail(email)) return;
      dispatch(authenticationSlice.actions.loginRequest({ email, password }));
    }
  };

  const submitEnabled = useMemo(
    () => !!email && !!password && isValidEmail(email),
    [email, password],
  );

  // if email & password passed as query parameters, attempt automatic login
  if (canAttemptAutomaticLogin) {
    if (email && password && isValidEmail(email)) {
      setCanAttemptAutomaticLogin(false);
      dispatch(authenticationSlice.actions.loginRequest({ email, password }));
    }
  }

  const handleEmailChange = (e: FormEvent<HTMLInputElement>) => {
    dispatch(authenticationSlice.actions.loginChanging());
    setEmail(e.currentTarget.value);
  };
  const handlePasswordChange = (e: FormEvent<HTMLInputElement>) => {
    dispatch(authenticationSlice.actions.loginChanging());
    setPassword(e.currentTarget.value);
  };
  const emailDefaultValue =
    typeof parsedQueryString?.email === "string"
      ? parsedQueryString.email
      : undefined;

  const passwordDefaultValue =
    typeof parsedQueryString?.password === "string"
      ? parsedQueryString.password
      : undefined;

  if (loggedIn === true) {
    const providedBuilding = parsedQueryString.building;
    let loggedInUrl = "/";

    if (typeof providedBuilding === "string") {
      loggedInUrl = loggedInUrl.concat(`?building=${providedBuilding}`);
    }
    return <Redirect to={{ pathname: loggedInUrl }} />;
  }
  return (
    <div className="login-page">
      <Container>
        <Row>
          <Col className="ml-auto mr-auto" lg="4" md="6">
            <Form onSubmit={handleSubmit}>
              <Card className="card-login">
                <CardHeader>
                  <CardHeader>
                    <img src={imageSrc} alt="Fetch Package" />
                    <br />
                    <br />
                  </CardHeader>
                </CardHeader>
                <CardBody>
                  {loginStatus === "invalid" ? (
                    <UncontrolledAlert color="danger" fade={false}>
                      Invalid email or password.
                    </UncontrolledAlert>
                  ) : null}
                  <label>Email address</label>
                  <FormGroup>
                    <Input
                      className={classes.getElementClassNames("email")}
                      placeholder="Enter email"
                      type="text"
                      onChange={handleEmailChange}
                      defaultValue={emailDefaultValue}
                    />
                  </FormGroup>
                  <label>Password</label>
                  <FormGroup>
                    <Input
                      className={classes.getElementClassNames("password")}
                      placeholder="Password"
                      type="password"
                      autoComplete="off"
                      onChange={handlePasswordChange}
                      defaultValue={passwordDefaultValue}
                    />
                  </FormGroup>
                  {loginStatus === "invalid" && (
                    <a
                      className={`${classes.getElementClassNames(
                        "forgotPassword",
                      )}`}
                      href="/forgot-password"
                    >
                      Forgot Password
                    </a>
                  )}
                  <Button
                    block={true}
                    className={classes.getElementClassNames("login")}
                    type={"submit"}
                    color="success"
                    disabled={!submitEnabled}
                  >
                    Login
                  </Button>
                </CardBody>
              </Card>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};
export default Login;
